import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import { buildGoalLedger, type LedgerView, type WebGoalLedger } from './goal-ledger';

/**
 * One goal's ledger, fetched and resolved into what the section should render.
 *
 * The read is its own request rather than a field on the goals list: this is ONE goal's history, and
 * folding it into the workspace read would multiply every grid refresh by a per-goal decisions query.
 * It is fetched only when a goal panel is open, which is the only place it is read.
 *
 * **A failed read is `unavailable`, never an empty ledger.** "We could not look" and "nothing has
 * happened" are different claims, and this codebase has already shipped one as the other once (see
 * `lib/goal-rounds.ts`). A refetch that fails after a good one keeps the good one — the same
 * keep-the-last-good rule `useWorkspaceGoals` follows — because a transient blip should not blank a
 * history the reader is in the middle of.
 *
 * A plan change is a task write, so the agent-task duplexer refetches: the ledger's whole job is to be
 * current about the plan moving.
 */
export function useGoalLedger(goalId: string | undefined, parentGoalId: string | undefined): LedgerView | undefined {
  const query = useQuery({
    queryKey: queryKeys.goalLedger(goalId, parentGoalId),
    queryFn: () => fetchGoalLedger(goalId as string, parentGoalId),
    enabled: !!goalId,
  });

  useAgentTaskDuplexer(() => {
    if (goalId) void query.refetch();
  });

  if (query.data) return buildGoalLedger(query.data);
  if (query.status === 'error') return { kind: 'unavailable' };
  // Still loading, or disabled. The section renders nothing rather than flashing an empty state at a
  // reader whose history simply has not arrived yet.
  return undefined;
}

async function fetchGoalLedger(goalId: string, parentGoalId: string | undefined): Promise<WebGoalLedger> {
  const params = new URLSearchParams({ goalId, ...(parentGoalId ? { parentGoalId } : {}) });
  const res = await apiFetch(`${API_BASE}/api/goal-ledger?${params.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as Partial<WebGoalLedger>;
  return { decisions: data.decisions ?? [], changes: data.changes ?? [] };
}
